import { useGameStore } from '@/store/gameStore'
import { useMemo } from 'react'
import Roll from './Roll'
import { GAME_CONFIG, ROLL_DIMENSIONS } from '@/types'
import { FallingRollConfig, defaultFallingRollConfig } from '@/components/DevTools/FallingRollEditor'
import { counterBg } from '@/assets/ui'

interface TowerProps {
  config?: FallingRollConfig
  isCollapsing?: boolean // Башня рассыпается при проигрыше
}

// Генерируем случайные параметры для физики рассыпания
const generateCollapsePhysics = (index: number, total: number) => {
  // Чем выше ролл, тем сильнее разлетается
  const heightFactor = (index + 1) / total
  
  return {
    // Случайное направление: -1 (влево) или 1 (вправо), с учётом позиции
    directionX: (Math.random() - 0.5) * 2,
    // Скорость по X (пиксели)
    velocityX: 50 + Math.random() * 150 * heightFactor,
    // Скорость падения (задержка анимации) — в 2 раза медленнее
    delay: index * 100 + Math.random() * 200,
    // Вращение (градусы)
    rotation: (Math.random() - 0.5) * 720,
    // Длительность падения — в 2 раза медленнее
    duration: 1200 + Math.random() * 800,
  }
}

export default function Tower({ config, isCollapsing = false }: TowerProps) {
  const { tower } = useGameStore()
  
  const cfg = config || defaultFallingRollConfig
  
  // Показываем только последние N роллов
  const visibleRolls = tower.slice(-GAME_CONFIG.TOWER_MAX_VISIBLE)
  
  // Генерируем физику рассыпания один раз при начале коллапса
  const collapsePhysics = useMemo(() => {
    if (!isCollapsing) return []
    return visibleRolls.map((_, index) => 
      generateCollapsePhysics(index, visibleRolls.length)
    )
  }, [isCollapsing, visibleRolls.length])
  
  if (tower.length === 0) {
    return null
  }
  
  // Коэффициент шага между роллами (0.82 = роллы перекрываются на 18%, убирая зазоры)
  const overlapFactor = GAME_CONFIG.TOWER_OVERLAP
  
  // Количество роллов, которые были "отрезаны" (не отображаются)
  const slicedCount = Math.max(0, tower.length - GAME_CONFIG.TOWER_MAX_VISIBLE)
  
  // Вычисляем вертикальные смещения — АБСОЛЮТНЫЕ (включая отрезанные роллы)
  // Используем реальные высоты SVG из ROLL_DIMENSIONS
  const getVerticalOffset = (index: number): number => {
    let offset = 0
    // Добавляем высоту отрезанных роллов (их нет в DOM, но позиция учитывается)
    for (let i = 0; i < slicedCount; i++) {
      offset += ROLL_DIMENSIONS[tower[i].variant].height * overlapFactor
    }
    // Добавляем высоту видимых роллов ниже текущего
    for (let i = 0; i < index; i++) {
      offset += ROLL_DIMENSIONS[visibleRolls[i].variant].height * overlapFactor
    }
    return offset
  }
  
  return (
    <div 
      className="absolute left-1/2 -translate-x-1/2 z-30"
      style={{
        bottom: `${cfg.plateBottom}px`,
      }}
    >
      {/* Контейнер башни */}
      <div 
        className="relative flex flex-col-reverse items-center"
      >
        {visibleRolls.map((roll, index) => {
          // Вертикальное смещение с учётом размеров всех предыдущих роллов
          const verticalOffset = getVerticalOffset(index)
          
          // Горизонтальное смещение — positionX в пикселях от центра
          const horizontalOffset = roll.positionX
          // Если ролл слишком близко к левому краю — баннер с коэффициентом
          // выйдет за экран. Переносим его направо от ролла. Порог
          // подобран эмпирически (баннер ~70px шириной + отступы).
          const flipBannerToRight = horizontalOffset < -60
          
          // Физика рассыпания
          const physics = isCollapsing ? collapsePhysics[index] : null
          
          // Стили для рассыпания
          const collapseStyle = physics ? {
            transform: `translateX(calc(-50% + ${physics.directionX * physics.velocityX}px)) rotate(${physics.rotation}deg)`,
            bottom: `-300px`, // Падает вниз за экран
            opacity: 0,
            transition: `all ${physics.duration}ms cubic-bezier(0.25, 0.1, 0.25, 1)`,
            transitionDelay: `${physics.delay}ms`,
          } : {}
          
          return (
            <div 
              key={roll.id} 
              className="absolute flex items-center"
              style={{
                bottom: `${verticalOffset}px`,
                left: `${horizontalOffset}px`,
                transform: 'translateX(-50%)',
                ...collapseStyle,
              }}
            >
              {/* Баннер коэффициента — слева от ролла по умолчанию,
                  справа если ролл сильно смещён влево (иначе уходит за экран) */}
              {!isCollapsing && (
                <div
                  className={`absolute flex items-center justify-center whitespace-nowrap ${
                    flipBannerToRight ? 'left-full ml-2' : 'right-full mr-2'
                  } ${roll.isBonus ? 'ring-2 ring-yellow-400' : ''}`}
                  style={{
                    backgroundImage: `url(${counterBg})`,
                    backgroundSize: '100% 100%',
                    padding: '4px 8px',
                    minWidth: '50px',
                  }}
                >
                  <span
                    style={{
                      fontFamily: "'Caesar Dressing', cursive",
                      fontWeight: 400,
                      fontSize: '16px',
                      lineHeight: '100%',
                      textAlign: 'center',
                      color: roll.isBonus ? '#FFD700' : roll.type === 'hot' ? '#FF6B35' : '#F8DDCA',
                    }}
                  >
                    ×{roll.coefficient.toFixed(2)}
                  </span>
                </div>
              )}
              
              {/* Сам ролл — размер из SVG viewBox */}
              <Roll roll={roll} />
            </div>
          )
        })}
      </div>
      
      {/* Счётчик роллов — скрываем при рассыпании */}
      {!isCollapsing && (
        <div 
          className="absolute -bottom-8 left-1/2 -translate-x-1/2 flex items-center justify-center whitespace-nowrap"
          style={{
            backgroundImage: `url(${counterBg})`,
            backgroundSize: '100% 100%',
            padding: '6px 12px',
            minWidth: '60px',
          }}
        >
          <span
            style={{
              fontFamily: "'Caesar Dressing', cursive",
              fontWeight: 400,
              fontSize: '19px',
              lineHeight: '100%',
              textAlign: 'center',
              color: '#F8DDCA',
            }}
          >
            {tower.length} / {Math.ceil((tower.length + 1) / 10) * 10}
          </span>
        </div>
      )}
    </div>
  )
}







